package features.attendance.handlers

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLButtonElement
import AppState
import Router
import shared.ui.ModalManager
import features.attendance.data.AttendanceRepository
import features.attendance.helpers.AttendanceHelpers

/**
 * Get geolocation coordinates with fallback mechanism
 * First tries high accuracy with 15s timeout, then falls back to low accuracy if it fails
 */
private fun getGeolocation(callback: (Double?, Double?, String) -> Unit) {
    val navigator = js("navigator")
    
    if (navigator.geolocation == null) {
        console.warn("Geolocation not supported")
        callback(null, null, "Office Location")
        return
    }
    
    // Try high accuracy first with longer timeout
    console.log("🌍 Attempting high accuracy GPS...")
    navigator.geolocation.getCurrentPosition(
        { position ->
            val coords = position.coords
            val lat = coords.latitude as Double
            val lng = coords.longitude as Double
            console.log("✅ High accuracy GPS success: $lat, $lng")
            callback(lat, lng, "GPS Location (High Accuracy)")
        },
        { error ->
            console.warn("⚠️ High accuracy GPS failed:", error.message, "- Trying low accuracy...")
            
            // Fallback to low accuracy
            navigator.geolocation.getCurrentPosition(
                { position ->
                    val coords = position.coords
                    val lat = coords.latitude as Double
                    val lng = coords.longitude as Double
                    console.log("✅ Low accuracy GPS success: $lat, $lng")
                    callback(lat, lng, "GPS Location (Low Accuracy)")
                },
                { fallbackError ->
                    console.error("❌ Low accuracy GPS also failed:", fallbackError.message)
                    // Use fallback location
                    callback(null, null, "Office Location (GPS Unavailable)")
                },
                js("{enableHighAccuracy: false, timeout: 10000, maximumAge: 60000}")
            )
        },
        js("{enableHighAccuracy: true, timeout: 15000, maximumAge: 0}")
    )
}

/**
 * Show loading state on button
 */
private fun setButtonLoading(buttonId: String, loading: Boolean, text: String) {
    val button = document.getElementById(buttonId) as? HTMLButtonElement
    if (button != null) {
        button.disabled = loading
        button.textContent = if (loading) "⏳ Processing..." else text
    }
}

/**
 * Handler for submitting clock in
 */
/**
 * Handler for submitting clock in
 */
fun submitClockIn() {
    console.log("🔧 submitClockIn() called")
    console.log("🔍 log-attendance: ========== CLOCK IN STARTED ==========")
    
    val teamId = AppState.currentTeam?.id
    val userId = AppState.currentUser?.id
    
    console.log("🔍 log-attendance: Team ID:", teamId)
    console.log("🔍 log-attendance: User ID:", userId)
    console.log("🔍 log-attendance: User name:", AppState.currentUser?.name)
    
    if (teamId == null || userId == null) {
        console.error("❌ log-attendance: Missing team or user ID")
        window.alert("No team or user selected")
        return
    }
    
    // Check if already clocked in today
    val todayRecord = AttendanceHelpers.getTodayAttendance(userId, teamId)
    console.log("🔍 log-attendance: Today's existing record:", todayRecord)
    
    if (todayRecord != null && todayRecord.checkIn != "-") {
        console.warn("⚠️ log-attendance: User already clocked in today at:", todayRecord.checkIn)
        window.alert("You have already clocked in today!")
        return
    }
    
    val noteInput = document.getElementById("clockin-reason-input") as? HTMLInputElement
    val note = noteInput?.value?.trim() ?: ""
    console.log("🔍 log-attendance: Note:", note)
    
    // Get location data
    val latInput = document.getElementById("clockin-lat") as? HTMLInputElement
    val lngInput = document.getElementById("clockin-lng") as? HTMLInputElement
    
    var lat = latInput?.value?.toDoubleOrNull()
    var lng = lngInput?.value?.toDoubleOrNull()
    
    console.log("🔍 log-attendance: GPS coordinates - Lat:", lat, "Lng:", lng)
    
    // If GPS failed, use fallback coordinates and allow user to continue
    if (lat == null || lng == null) {
        console.warn("⚠️ log-attendance: GPS not available, using fallback coordinates")
        lat = 0.0
        lng = 0.0
    }
    
    setButtonLoading("clockin-submit-btn", true, "Capturing photo...")
    console.log("🔍 log-attendance: Capturing photo...")
    
    // Auto-capture photo before submitting
    js("window.capturePhoto()").then { photoData ->
        console.log("🔍 log-attendance: Photo captured successfully")
        setButtonLoading("clockin-submit-btn", true, "Submitting...")
        
        val currentTime = AttendanceRepository.getCurrentTime()
        val location = "Location ($${lat.toString().take(8)}, $${lng.toString().take(8)})"
        
        console.log("🔍 log-attendance: Current time:", currentTime)
        console.log("🔍 log-attendance: Location string:", location)
        console.log("🔍 log-attendance: Calling AttendanceRepository.clockIn()...")
        
        AttendanceRepository.clockIn(
            teamId = teamId,
            userId = userId,
            checkInTime = currentTime,
            location = location,
            latitude = lat,
            longitude = lng,
            photo = photoData as String,
            note = note
        ).then { recordId ->
            console.log("✅ log-attendance: ========== CLOCK IN SUCCESS ==========")
            console.log("✅ log-attendance: Record ID:", recordId)
            console.log("✅ log-attendance: User:", AppState.currentUser?.name)
            console.log("✅ log-attendance: Time:", currentTime)
            console.log("✅ log-attendance: Location:", location)
            
            // Update AppState with new record
            console.log("🔍 log-attendance: Fetching created record from Firebase...")
            AttendanceRepository.getTodayAttendance(teamId, userId).then { record ->
                if (record != null) {
                    console.log("✅ log-attendance: Record fetched from Firebase:", record)
                    val teamAttendance = AppState.attendance[teamId] ?: mutableListOf()
                    console.log("🔍 log-attendance: Current AppState.attendance[$teamId] size:", teamAttendance.size)
                    teamAttendance.add(record)
                    AppState.attendance[teamId] = teamAttendance
                    console.log("✅ log-attendance: Record added to AppState")
                    console.log("✅ log-attendance: New AppState.attendance[$teamId] size:", teamAttendance.size)
                } else {
                    console.warn("⚠️ log-attendance: Could not fetch record from Firebase")
                }
                
                // Close modal and refresh team selector only
                ModalManager.close()
                setButtonLoading("clockin-submit-btn", false, "Confirm Clock In")
                window.alert("✅ Clock in successful at $currentTime!")
                
                console.log("🔍 log-attendance: Re-rendering UI...")
                // Re-render the page to show updated attendance
                Router.render()
                console.log("✅ log-attendance: ========== CLOCK IN COMPLETE ==========")
            }.catch { error ->
                console.error("Error refreshing attendance:", error)
                ModalManager.close()
                setButtonLoading("clockin-submit-btn", false, "Confirm Clock In")
                window.alert("✅ Clock in successful at $currentTime!")
                
                // Re-render the page to show updated attendance
                Router.render()
            }
        }.catch { error ->
            console.error("❌ Clock in failed:", error)
            setButtonLoading("clockin-submit-btn", false, "Confirm Clock In")
            window.alert("❌ Clock in failed. Please try again.")
        }
    }.catch { error ->
        console.error("❌ Photo capture failed:", error)
        setButtonLoading("clockin-submit-btn", false, "Confirm Clock In")
        window.alert("❌ Failed to capture photo. Please try again.")
    }
}

/**
 * Handler for submitting clock out
 */
fun submitClockOut() {
    console.log("🔧 submitClockOut() called")
    
    val teamId = AppState.currentTeam?.id
    val userId = AppState.currentUser?.id
    
    if (teamId == null || userId == null) {
        window.alert("No team or user selected")
        return
    }
    
    // Get today's record to find the record ID
    val todayRecord = AttendanceHelpers.getTodayAttendance(userId, teamId)
    if (todayRecord == null || todayRecord.checkIn == "-") {
        window.alert("You haven't clocked in yet today!")
        return
    }
    
    if (todayRecord.checkOut != "-") {
        window.alert("You have already clocked out today!")
        return
    }
    
    // Get location data
    val latInput = document.getElementById("clockin-lat") as? HTMLInputElement
    val lngInput = document.getElementById("clockin-lng") as? HTMLInputElement
    
    val lat = latInput?.value?.toDoubleOrNull()
    val lng = lngInput?.value?.toDoubleOrNull()
    
    if (lat == null || lng == null) {
        window.alert("Please wait for location to be detected!")
        return
    }
    
    setButtonLoading("clockin-submit-btn", true, "Capturing photo...")
    
    // Auto-capture photo before submitting
    js("window.capturePhoto()").then { photoData ->
        setButtonLoading("clockin-submit-btn", true, "Submitting...")
        
        val currentTime = AttendanceRepository.getCurrentTime()
        
        // Find record ID from Firestore
        AttendanceRepository.getTodayAttendance(teamId, userId).then { firestoreRecord ->
            if (firestoreRecord != null) {
                val recordId = firestoreRecord.id.toString()
                
                AttendanceRepository.clockOut(
                    teamId = teamId,
                    recordId = recordId,
                    checkOutTime = currentTime,
                    latitude = lat,
                    longitude = lng,
                    photo = photoData as String
                ).then {
                    console.log("✅ Clock out successful")
                    
                    // Update AppState
                    val teamAttendance = AppState.attendance[teamId] ?: mutableListOf()
                    val index = teamAttendance.indexOfFirst { 
                        it.userId == userId && it.date == todayRecord.date 
                    }
                    if (index >= 0) {
                        teamAttendance[index] = todayRecord.copy(checkOut = currentTime)
                    }
                    
                    // Close modal and refresh
                    ModalManager.close()
                    setButtonLoading("clockin-submit-btn", false, "Confirm Clock Out")
                    window.alert("✅ Clock out successful at $currentTime!")
                    
                    // Re-render the page to update button state
                    Router.render()
                }.catch { error ->
                    console.error("❌ Clock out failed:", error)
                    setButtonLoading("clockin-submit-btn", false, "Confirm Clock Out")
                    window.alert("❌ Clock out failed. Please try again.")
                }
            } else {
                setButtonLoading("clockin-submit-btn", false, "Confirm Clock Out")
                window.alert("❌ Could not find today's attendance record")
            }
        }.catch { error ->
            console.error("❌ Error finding record:", error)
            setButtonLoading("clockin-submit-btn", false, "Confirm Clock Out")
            window.alert("❌ Clock out failed. Please try again.")
        }
    }.catch { error ->
        console.error("❌ Photo capture failed:", error)
        setButtonLoading("clockin-submit-btn", false, "Confirm Clock Out")
        window.alert("❌ Failed to capture photo. Please try again.")
    }
}
